import { useMutation } from "@tanstack/react-query";
import { useMemo, useRef, useState, type DragEvent } from "react";
import { Link, useSearchParams } from "react-router";

import { CategoryPicker } from "../components/Categories";
import { Loading } from "../components/Loading";
import { useToast } from "../components/Toast";
import { api, type ImportJob, type ImportPreview, type ImportRow, type ImportRowStatus } from "../lib/api";
import { plural } from "../lib/format";
import { useImportJob } from "../lib/queries";
import { formatSiren } from "../lib/siren";
import { useTitle } from "../lib/useTitle";

const ACCEPT = ".csv,.xlsx,.xlsm,.txt";
const MAX_SHOWN = 200;

const ROW_STATUS: Record<ImportRowStatus, { label: string; tone: string }> = {
  new: { label: "À ajouter", tone: "ok" },
  update: { label: "Déjà suivie", tone: "medium" },
  duplicate: { label: "En double", tone: "low" },
  invalid: { label: "SIREN invalide", tone: "critical" },
};

const RESULT_STATUS: Record<ImportJob["results"][number]["status"], string> = {
  created: "Ajoutée",
  updated: "Mise à jour",
  not_found: "Introuvable au répertoire",
  error: "Erreur",
};

export function Import() {
  useTitle("Importer un fichier");
  const [params, setParams] = useSearchParams();
  const jobId = Number(params.get("suivi")) || null;
  const [preview, setPreview] = useState<ImportPreview | null>(null);

  const step = jobId ? 3 : preview ? 2 : 1;
  const reset = () => {
    setPreview(null);
    setParams({}, { replace: true });
  };

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Importer des entreprises</h1>
          <p>Ajoutez d'un coup les SIREN d'un tableur, et rangez-les dans vos catégories.</p>
        </div>
        <Link className="button button--quiet" to="/tableau-de-bord">
          Retour à mes entreprises
        </Link>
      </div>

      <ol className="stepper" aria-label="Étapes de l'import">
        {["Choisir le fichier", "Vérifier les lignes", "Importer"].map((label, i) => (
          <li key={label} aria-current={step === i + 1 ? "step" : undefined} className={step > i + 1 ? "is-done" : ""}>
            {label}
          </li>
        ))}
      </ol>

      {step === 1 && <ChooseFile onPreview={setPreview} />}
      {step === 2 && preview && (
        <Review
          preview={preview}
          onCancel={reset}
          onStarted={(job) => {
            setPreview(null);
            setParams({ suivi: String(job.id) });
          }}
        />
      )}
      {step === 3 && jobId && <Progress jobId={jobId} onAgain={reset} />}
    </div>
  );
}

function ChooseFile({ onPreview }: { onPreview: (p: ImportPreview) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const upload = useMutation({ mutationFn: api.previewImport, onSuccess: onPreview });

  function pick(files: FileList | null) {
    const file = files?.[0];
    if (file) upload.mutate(file);
  }

  function onDrop(e: DragEvent) {
    e.preventDefault();
    setDragging(false);
    pick(e.dataTransfer.files);
  }

  return (
    <div className="import-grid">
      <div>
        <div
          className={`dropzone${dragging ? " is-dragging" : ""}`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
        >
          {upload.isPending ? (
            <Loading label="Lecture du fichier" />
          ) : (
            <>
              <p className="dropzone__title">Déposez votre fichier ici</p>
              <p className="field__hint">Excel (.xlsx) ou CSV, 5 000 lignes et 5 Mo au maximum.</p>
              <label className="button" htmlFor="import-file">
                Choisir un fichier
              </label>
              <input
                ref={input}
                id="import-file"
                className="visually-hidden"
                type="file"
                accept={ACCEPT}
                onChange={(e) => {
                  pick(e.target.files);
                  e.target.value = "";
                }}
              />
            </>
          )}
        </div>
        {upload.isError && (
          <div className="notice notice--error import-error" role="alert">
            {upload.error.message}
          </div>
        )}
      </div>

      <aside className="import-help">
        <h2>Format attendu</h2>
        <p>Une ligne par entreprise. Seule la colonne SIREN est obligatoire ; un SIRET convient aussi.</p>
        <table className="sample-table">
          <caption className="visually-hidden">Exemple de fichier</caption>
          <thead>
            <tr>
              <th scope="col">SIREN</th>
              <th scope="col">Libellé</th>
              <th scope="col">Catégories</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>552 032 534</td>
              <td>Client yaourts</td>
              <td>Clients, Agroalimentaire</td>
            </tr>
            <tr>
              <td>356000000</td>
              <td></td>
              <td>Fournisseurs</td>
            </tr>
          </tbody>
        </table>
        <ul>
          <li>
            <strong>Libellé</strong> (ou Nom, Référence) : le nom que vous donnez à l'entreprise.
          </li>
          <li>
            <strong>Catégories</strong> (ou Groupe, Tags) : séparez-en plusieurs par une virgule. Les catégories
            inconnues sont créées.
          </li>
        </ul>
        <a href="/modele-import-siren.csv" download>
          Télécharger le modèle CSV
        </a>
      </aside>
    </div>
  );
}

type RowFilter = "import" | "problems" | "all";

function Review({
  preview,
  onCancel,
  onStarted,
}: {
  preview: ImportPreview;
  onCancel: () => void;
  onStarted: (job: ImportJob) => void;
}) {
  const [categoryIds, setCategoryIds] = useState<number[]>([]);
  const [filter, setFilter] = useState<RowFilter>("import");
  const toast = useToast();
  const start = useMutation({
    mutationFn: api.startImport,
    onSuccess: (job) => {
      toast(`Import de ${plural(job.total, "entreprise", "entreprises")} lancé.`);
      onStarted(job);
    },
  });

  const counts = useMemo(() => {
    const c: Record<ImportRowStatus, number> = { new: 0, update: 0, duplicate: 0, invalid: 0 };
    for (const row of preview.rows) c[row.status] += 1;
    return c;
  }, [preview]);
  const importable = preview.rows.filter((r) => r.status === "new" || r.status === "update");
  const shown = preview.rows.filter((r) =>
    filter === "all" ? true : filter === "import" ? r.status === "new" || r.status === "update" : !(r.status === "new" || r.status === "update"),
  );

  const columns = [
    `SIREN lus dans « ${preview.siren_column} »`,
    preview.label_column && `libellés dans « ${preview.label_column} »`,
    preview.category_column && `catégories dans « ${preview.category_column} »`,
  ].filter(Boolean);

  return (
    <div className="review">
      <section className="tally" aria-label="Résumé du fichier">
        <p className="tally__file">
          <strong>{preview.filename}</strong> : {plural(preview.rows.length, "ligne", "lignes")}, {columns.join(", ")}.
        </p>
        <dl className="tally__counts">
          {(Object.keys(ROW_STATUS) as ImportRowStatus[]).map((status) => (
            <div key={status} className={`tally__count tally__count--${ROW_STATUS[status].tone}`}>
              <dt>{ROW_STATUS[status].label}</dt>
              <dd>{counts[status]}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="sheet review__options">
        <CategoryPicker
          selected={categoryIds}
          onChange={setCategoryIds}
          legend="Ajouter aussi ces catégories à toutes les lignes importées (facultatif)"
        />
      </section>

      <section aria-labelledby="rows-title">
        <div className="timeline-head">
          <h2 id="rows-title">Lignes du fichier</h2>
          <div className="segmented" role="group" aria-label="Filtrer les lignes">
            <button type="button" aria-pressed={filter === "import"} onClick={() => setFilter("import")}>
              À importer <span className="count">{importable.length}</span>
            </button>
            <button type="button" aria-pressed={filter === "problems"} onClick={() => setFilter("problems")}>
              Ignorées <span className="count">{counts.duplicate + counts.invalid}</span>
            </button>
            <button type="button" aria-pressed={filter === "all"} onClick={() => setFilter("all")}>
              Toutes <span className="count">{preview.rows.length}</span>
            </button>
          </div>
        </div>
        <RowsTable rows={shown.slice(0, MAX_SHOWN)} />
        {shown.length > MAX_SHOWN && (
          <p className="field__hint table-more">
            {shown.length - MAX_SHOWN} lignes supplémentaires non affichées.
          </p>
        )}
      </section>

      {start.isError && (
        <div className="notice notice--error" role="alert">
          {start.error.message}
        </div>
      )}
      <div className="review__actions">
        <button
          type="button"
          className="button"
          disabled={!importable.length || start.isPending}
          onClick={() =>
            start.mutate({
              filename: preview.filename,
              category_ids: categoryIds,
              rows: importable.map((r) => ({
                line: r.line,
                siren: r.siren!,
                label: r.label,
                categories: r.categories,
              })),
            })
          }
        >
          {importable.length
            ? `Importer ${plural(importable.length, "entreprise", "entreprises")}`
            : "Aucune ligne à importer"}
        </button>
        <button type="button" className="button button--quiet" onClick={onCancel}>
          Choisir un autre fichier
        </button>
      </div>
    </div>
  );
}

function RowsTable({ rows }: { rows: ImportRow[] }) {
  if (!rows.length) return <p className="field__hint">Aucune ligne dans ce filtre.</p>;
  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            <th scope="col">Ligne</th>
            <th scope="col">SIREN</th>
            <th scope="col">Libellé</th>
            <th scope="col">Catégories</th>
            <th scope="col">Statut</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.line}>
              <td className="num">{row.line}</td>
              <td className="num">{row.siren ? formatSiren(row.siren) : row.raw || "vide"}</td>
              <td>{row.label}</td>
              <td>{row.categories.join(", ")}</td>
              <td>
                <span className={`tag tag--${ROW_STATUS[row.status].tone}`}>{ROW_STATUS[row.status].label}</span>
                {row.message && row.status !== "new" && <div className="field__hint">{row.message}</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Progress({ jobId, onAgain }: { jobId: number; onAgain: () => void }) {
  const job = useImportJob(jobId);

  if (job.isPending) return <Loading label="Chargement de l'import" />;
  if (job.isError) {
    return (
      <div className="notice notice--error" role="alert">
        {job.error.message} <button type="button" className="button button--link" onClick={onAgain}>Importer un autre fichier</button>
      </div>
    );
  }

  const data = job.data;
  const done = data.status === "done";
  const percent = data.total ? Math.round((data.processed / data.total) * 100) : 100;
  const count = (status: string) => data.results.filter((r) => r.status === status).length;
  const problems = data.results.filter((r) => r.status === "not_found" || r.status === "error");

  return (
    <div className="progress-panel">
      <section className="sheet progress-card" aria-live="polite">
        <h2>{done ? "Import terminé" : `Import en cours : ${percent} %`}</h2>
        <div
          className="progress"
          role="progressbar"
          aria-label={`Import de ${data.filename}`}
          aria-valuemin={0}
          aria-valuemax={data.total}
          aria-valuenow={data.processed}
        >
          {Array.from({ length: 20 }, (_, i) => (
            <span key={i} className={i < Math.round(percent / 5) ? "is-filled" : ""} />
          ))}
        </div>
        <p className="field__hint">
          {data.processed} sur {data.total} lignes de {data.filename}.{" "}
          {!done && "Chaque entreprise est vérifiée au répertoire : vous pouvez quitter cette page, l'import continue."}
        </p>
        <dl className="tally__counts">
          <div className="tally__count tally__count--ok">
            <dt>Ajoutées</dt>
            <dd>{count("created")}</dd>
          </div>
          <div className="tally__count tally__count--medium">
            <dt>Mises à jour</dt>
            <dd>{count("updated")}</dd>
          </div>
          <div className="tally__count tally__count--critical">
            <dt>Non importées</dt>
            <dd>{problems.length}</dd>
          </div>
        </dl>
        {done && (
          <div className="actions">
            <Link className="button" to="/tableau-de-bord">
              Voir mes entreprises
            </Link>
            <button type="button" className="button button--quiet" onClick={onAgain}>
              Importer un autre fichier
            </button>
            {problems.length > 0 && (
              <button type="button" className="button button--link" onClick={() => downloadProblems(data)}>
                Télécharger les lignes non importées
              </button>
            )}
          </div>
        )}
      </section>

      {problems.length > 0 && (
        <section aria-labelledby="problems-title">
          <h2 id="problems-title" className="problems-title">
            Lignes non importées
          </h2>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">Ligne</th>
                  <th scope="col">SIREN</th>
                  <th scope="col">Motif</th>
                </tr>
              </thead>
              <tbody>
                {problems.slice(0, MAX_SHOWN).map((r) => (
                  <tr key={`${r.line}-${r.siren}`}>
                    <td className="num">{r.line}</td>
                    <td className="num">{formatSiren(r.siren)}</td>
                    <td>
                      <span className="tag tag--critical">{RESULT_STATUS[r.status]}</span>
                      {r.message && <div className="field__hint">{r.message}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

function downloadProblems(job: ImportJob) {
  const lines = ["Ligne;SIREN;Motif"];
  for (const r of job.results) {
    if (r.status !== "not_found" && r.status !== "error") continue;
    lines.push([r.line ?? "", r.siren, (r.message ?? RESULT_STATUS[r.status]).replace(/;/g, ",")].join(";"));
  }
  const blob = new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `non-importees-${job.filename.replace(/\.[^.]+$/, "")}.csv`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
